import React, { MouseEventHandler } from 'react';
import MuiTab from '@mui/material/Tab';
import { makeStyles } from 'tss-react/mui';
import { CommonPropsAndTypes } from '../../_Types/StandardComponentProps';

/**
En tab, ska alltid användas inuti en Tabs-komponent
se exempel för Tabs

se även https://material-ui-1dab0.firebaseapp.com/api/tab/
*/
interface IProps {
  label?: React.ReactNode;
  textColor?: CommonPropsAndTypes.ColorType;
  icon?: React.ReactElement<any>;
  disabled?: boolean;
  spaciousPadding?: boolean;
  small?: boolean;
  subtleTab?: boolean;
  required?: boolean;
  hidden?: boolean;
  onClick?: MouseEventHandler<HTMLElement>;
  value?: number;
  testId?: string;
  selected?: boolean;
  className?: string;
  classes?: any;
  style?: React.CSSProperties;
}

const useStyles = makeStyles<
  { subtleTab: boolean },
  'subtleTabStyle' | 'disabled' | 'selected'
>({
  uniqId: '6021a937-ecd2-4d21-b69a-0c06bf8c33a6',
})(
  (theme, props, classes) =>
    ({
      root: {
        maxWidth: 'none',
        padding: theme.spacing(0.75, 1.5),
        [`&.$${classes.disabled}`]: {
          opacity: 0.5,
        },
      },

      spaciousPadding: {
        padding: theme.spacing(0, 3),
      },
      subtleTabStyle: {
        textTransform: 'none',
      },
      small: {
        minWidth: theme.spacing(10),
      },
      labelContainer: {},
      selected: {
        '&&': {
          backgroundColor:
            theme.customProperties.Fade.LightOnDark.mediumEmphasis,
          color: props.subtleTab
            ? theme.palette.primary.main
            : theme.customProperties.Fade.DarkOnLight.highEmphasis,
        },
      },
      textColorInherit: {
        color: theme.customProperties.Fade.LightOnDark.highEmphasis,
        opacity: 0.96,
        [`&.${classes.subtleTabStyle}`]: {
          color: theme.customProperties.Fade.DarkOnLight.highEmphasis,
        },
      },
      disabled: {
        color: theme.customProperties.Fade.LightOnDark.lowEmphasis,
      },
      asterisk: {
        color: theme.palette.error.main,
      },
    } as const)
);

const EDTab = (props: IProps) => {
  const { spaciousPadding, small, label, subtleTab, hidden, value, ...rest } =
    props;
  const { classes, cx } = useStyles({ subtleTab: !!props.subtleTab });
  const {
    root,
    spaciousPadding: spaciousPaddingClass,
    small: smallClass,
    textColorInherit,
    disabled: disabledClass,
    labelContainer,
    subtleTabStyle,
    asterisk,
    ...muiClasses
  } = classes;
  if (hidden) {
    return <></>;
  }
  return (
    <MuiTab
      classes={{
        root: cx(root, {
          [classes.spaciousPadding]: spaciousPadding,
          [classes.small]: small,
          [classes.textColorInherit]: !props.disabled,
          [classes.disabled]: props.disabled,
          [classes.subtleTabStyle]: props.subtleTab,
        }),
        ...muiClasses,
      }}
      value={value}
      label={
        label ? (
          <span className={labelContainer}>
            {label}
            {props.required && (
              <span aria-hidden className={cx(asterisk)}>
                &thinsp;{'*'}
              </span>
            )}
          </span>
        ) : null
      }
      data-testid={props.testId}
      {...rest}
    />
  );
};

export default EDTab;
